import { getItemPOFValue, ItemPropertyOrFunction } from "../object.tools";

export type ListMapOptions = {
	/** if false, object with null or undefined keys are ignored */
	acceptNullKey: boolean,
	/** if false, only not null and not undefined objects are added */
	acceptNullObjects?: boolean,
	/** if true and we encounter an array as a key, we iterate over keys (only one depth) */
	flattenKeyArrays?: boolean,
	/** 
	 * if true, an 'empty array' key is equivalent to 'undefined' key (only valid for addObject[s]) 
	 * if false, 'empty arrays' keys are ignored
	 */
	emptyKeyArrayIsUndefined?: boolean,
	/** if true, an 'null' key is equivalent to 'undefined' key (only valid for addObject[s]) */
	nullKeyIsUndefined?: boolean
};

export type ListAddObjectParams<K, T> = {
	/** objects to add */
	objects: T[] | undefined,
	/** property name or function that returns object key (or keys if options.flattenKeyArrays) */
	key: ItemPropertyOrFunction<T, K> | ItemPropertyOrFunction<T, K[]>
};

/**
 * Equivalent to Map<K, List<T>> with specialized functions
 */
export abstract class AbstractListMap<K, T, TList> extends Map<K, TList> {

	/**
	 * @param options map options (how to deal with key arrays, null or undefined values)
	 * @param addObjects if objects are provided constructor will call addObjects
	 */
	constructor(
		public options: ListMapOptions,
		addObjects?: ListAddObjectParams<K, T>
	) {
		super();
		if (addObjects) {
			this.addObjects(addObjects.objects, addObjects.key);
		}
	}

	/** 
	 * add objects to the map 
	 * @param objects objects to add
	 * @param key property name or function that returns object key (or keys if options.flattenKeyArrays)
	 */
	public addObjects(objects: T[] | undefined, key: ItemPropertyOrFunction<T, K> | ItemPropertyOrFunction<T, K[]>): void {
		if (objects) {
			for (const o of objects) {
				this.addObject(o, key);
			}
		}
	}

	/**
	 * add object
	 * @param o object to add
	 * @param key property name or function that returns object key (or keys if options.flattenKeyArrays)
	 */
	public addObject(o: T, key: ItemPropertyOrFunction<T, K> | ItemPropertyOrFunction<T, K[]>): void {
		if (o == null && !this.options.acceptNullObjects) {
			return;
		}
		const k: K | K[] | undefined = getItemPOFValue(o, key as ItemPropertyOrFunction<T, K>);
		if (k != null) {
			if (this.options.flattenKeyArrays && Array.isArray(k)) {
				if (k.length === 0) {
					if (this.options.emptyKeyArrayIsUndefined) {
						// empty => store as undefined key
						this._addNullKeyEntryIfAccepted(undefined, o);
					}
				} else {
					for (const singleK of k) {
						if (singleK != null) {
							this.addEntry(singleK, o);
						} else {
							this._addNullKeyEntryIfAccepted(singleK, o);
						}
					}
				}
			} else {
				this.addEntry(k, o);
			}
		} else {
			this._addNullKeyEntryIfAccepted(k as null | undefined, o);
		}
	}

	/**
	 * adds an null or undefined key entry
	 * replaces null by undefined if this.options.nullKeyIsUndefined
	 */
	protected _addNullKeyEntryIfAccepted(k: undefined | null, o: T): void {
		if (this.options.acceptNullKey) {
			this.addEntry((this.options.nullKeyIsUndefined ? undefined : k) as K, o);
		}
	}

	/**
	 * add objects to specified key list
	 */
	public addEntries(key: K, objects: T[]): void {
		if (objects) {
			for (const o of objects) {
				this.addEntry(key, o);
			}
		}
	}

	/**
	 * add object to specified key list
	 */
	public abstract addEntry(key: K, o: T): void;

}